import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { SCHEDULE_DEAL_MEETING } from '../../../graphql/mutations/dealMutations';
import Button from '../../../../../ui/canon/Button';
import { SingleColumnTemplate } from '../../../../../ui/layouts/SingleColumnTemplate';

const ScheduleMeeting = ({ deal, dealInterest, companySettings, currentUser, refetchData }) => {
  const intl = useIntl();
  const history = useHistory();

  const [formData, setFormData] = useState({
    format: 'video',
    date: '',
    time: '',
    duration: 60,
    description: '',
    location: ''
  });
  
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  
  const [scheduleMeeting, { loading: scheduling }] = useMutation(SCHEDULE_DEAL_MEETING, {
    onCompleted: (data) => {
      console.log('Mutation completed with data:', data); // Debug log
      if (data?.scheduleDealMeeting?.scheduledMeeting) {
        setSuccess(true);
        setTimeout(() => {
          history.push(`/succession/deals/${deal.deal_name}/prospects/negotiate`);
        }, 2500);
      } else if (data?.scheduleDealMeeting?.errors) {
        setErrors({ submit: data.scheduleDealMeeting.errors.join(', ') });
      }
    },
    onError: (error) => {
      setErrors({ submit: `Failed to schedule meeting: ${error.message}` });
    }
  });
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field-specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.date) {
      newErrors.date = intl.formatMessage({ id: 'succession.schedule_meeting.validation_date_required' });
    } else {
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.date = intl.formatMessage({ id: 'succession.schedule_meeting.validation_date_future' });
      }
    }

    if (!formData.time) {
      newErrors.time = intl.formatMessage({ id: 'succession.schedule_meeting.validation_time_required' });
    }

    if (formData.format === 'onsite' && !formData.location.trim()) {
      newErrors.location = intl.formatMessage({ id: 'succession.schedule_meeting.location_required' });
    }
    
    return newErrors;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    // Combine date and time
    const startTime = new Date(`${formData.date}T${formData.time}`);
    
    const input = {
      dealId: deal.id,
      dealInterestId: dealInterest.id,
      startTime: startTime.toISOString(),
      duration: parseInt(formData.duration, 10),
      format: formData.format,
      location: formData.location.trim() || null,
      description: formData.description.trim() || null
    };
    
    scheduleMeeting({
      variables: { input }
    });
  };
  
  const formatOptions = [
    {
      value: 'phone',
      label: intl.formatMessage({ id: 'succession.schedule_meeting.phone_call' }),
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      description: intl.formatMessage({ id: 'succession.schedule_meeting.phone_call_description' }),
    },
    {
      value: 'onsite',
      label: intl.formatMessage({ id: 'succession.schedule_meeting.in_person' }),
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      description: intl.formatMessage({ id: 'succession.schedule_meeting.in_person_description' }),
    },
    {
      value: 'video',
      label: intl.formatMessage({ id: 'succession.schedule_meeting.video_call' }),
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      description: intl.formatMessage({ id: 'succession.schedule_meeting.video_call_description' }),
    }
  ];
  
  const durationOptions = [
    { value: 30, label: intl.formatMessage({ id: 'succession.schedule_meeting.duration_30' }), subtitle: intl.formatMessage({ id: 'succession.schedule_meeting.duration_30_subtitle' }) },
    { value: 45, label: intl.formatMessage({ id: 'succession.schedule_meeting.duration_45' }), subtitle: intl.formatMessage({ id: 'succession.schedule_meeting.duration_45_subtitle' }) },
    { value: 60, label: intl.formatMessage({ id: 'succession.schedule_meeting.duration_60' }), subtitle: intl.formatMessage({ id: 'succession.schedule_meeting.duration_60_subtitle' }) },
    { value: 90, label: intl.formatMessage({ id: 'succession.schedule_meeting.duration_90' }), subtitle: intl.formatMessage({ id: 'succession.schedule_meeting.duration_90_subtitle' }) }
  ];
  
  // Generate time slots from 8 AM to 6 PM in 30-minute intervals
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === 18 && minute > 0) break; // Stop at 6:00 PM
        
        const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        
        // Convert to 12-hour format for display
        const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayTime = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
        
        // Determine time period
        let period = 'morning';
        if (hour >= 12 && hour < 17) period = 'afternoon';
        if (hour >= 17) period = 'evening';
        
        slots.push({
          value: time24,
          label: displayTime,
          hour: hour,
          minute: minute,
          period: period
        });
      }
    }
    return slots;
  };
  
  const timeSlots = generateTimeSlots();
  
  // Group time slots by period
  const groupedTimeSlots = {
    morning: timeSlots.filter(slot => slot.period === 'morning'),
    afternoon: timeSlots.filter(slot => slot.period === 'afternoon'),
    evening: timeSlots.filter(slot => slot.period === 'evening')
  };
  
  if (success) {
    return (
      <SingleColumnTemplate
        title={intl.formatMessage({ id: 'succession.schedule_meeting.meeting_scheduled' })}
        variant="default"
      >
        <div className="bg-[var(--succession-bg-primary)] rounded-2xl shadow-xl overflow-hidden border border-[var(--succession-border)]">
          <div className="px-8 py-12 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <p className="text-lg text-[var(--succession-text-secondary)] mb-2">
                {intl.formatMessage({ id: 'succession.schedule_meeting.meeting_scheduled_body' }, { name: dealInterest.inventory_user.user.allowed_name || dealInterest.inventory_user.user.name })}
              </p>
              <p className="text-sm text-[var(--succession-text-muted)] mb-8">
                {intl.formatMessage({ id: 'succession.schedule_meeting.redirecting_body' })}
              </p>
              
              <div className="inline-flex items-center" style={{ color: 'var(--succession-primary)' }}>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {intl.formatMessage({ id: 'succession.schedule_meeting.redirecting' })}
              </div>
            </div>
          </div>
      </SingleColumnTemplate>
    );
  }
  
  return (
    <SingleColumnTemplate
      title={intl.formatMessage({ id: 'succession.schedule_meeting.title' })}
      subtitle={
        <>
          {intl.formatMessage({ id: 'succession.schedule_meeting.subtitle' }, { name: <span key="name" className="font-semibold" style={{ color: 'var(--succession-primary)' }}>{dealInterest.inventory_user.user.allowed_name || dealInterest.inventory_user.user.name}</span> })}
        </>
      }
      variant="wide"
    >

        {/* Deal Context Card */}
        <div className="bg-[var(--succession-bg-primary)] rounded-xl shadow-sm border border-[var(--succession-border)] p-6 mb-8">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: 'var(--succession-primary)' }}>
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2h8zM8 14v.01M12 14v.01M16 14v.01" />
                </svg>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-1">
                {deal.headline}
              </h3>
              {deal.sub_headline && (
                <p className="text-[var(--succession-text-secondary)] mb-2">{deal.sub_headline}</p>
              )}
              <div className="flex items-center text-sm text-[var(--succession-text-muted)]">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: 'var(--succession-bg-secondary)', color: 'var(--succession-primary)' }}>
                  {dealInterest.workflow_state.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
                <span className="mx-2">•</span>
                <span>{dealInterest.inventory_user.user.city}, {dealInterest.inventory_user.user.province}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Form */}
        <div className="bg-[var(--succession-bg-primary)] rounded-2xl shadow-xl overflow-hidden">
          <form onSubmit={handleSubmit} className="divide-y divide-[var(--succession-border)]">
            
            {/* Meeting Format Section */}
            <div className="p-8">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-2">{intl.formatMessage({ id: 'succession.schedule_meeting.meeting_format' })}</h3>
                <p className="text-[var(--succession-text-secondary)]">{intl.formatMessage({ id: 'succession.schedule_meeting.meeting_format_subtitle' })}</p>
              </div>
              
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {formatOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all duration-200 hover:shadow-md ${
                      formData.format === option.value
                        ? 'shadow-md'
                        : 'border-[var(--succession-border)] bg-[var(--succession-bg-primary)] hover:border-[var(--succession-border-hover)]'
                    }`}
                    style={formData.format === option.value ? {
                      borderColor: 'var(--succession-primary)',
                      backgroundColor: 'var(--succession-bg-secondary)'
                    } : {}}
                  >
                    <input
                      type="radio"
                      name="format"
                      value={option.value}
                      checked={formData.format === option.value}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className="text-center">
                      <div className="mb-3 flex items-center justify-center" style={{ color: 'var(--succession-primary)' }}>
                        {option.icon}
                      </div>
                      <div className={`text-lg font-semibold mb-1 ${
                        formData.format === option.value ? '' : 'text-[var(--succession-text-primary)]'
                      }`}
                      style={formData.format === option.value ? { color: 'var(--succession-primary)' } : {}}>
                        {option.label}
                      </div>
                      <div className={`text-sm ${
                        formData.format === option.value ? 'text-[var(--succession-text-secondary)]' : 'text-[var(--succession-text-muted)]'
                      }`}>
                        {option.description}
                      </div>
                    </div>
                    {formData.format === option.value && (
                      <div className="absolute top-4 right-4">
                        <svg className="w-5 h-5" style={{ color: 'var(--succession-primary)' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Date & Time Section */}
            <div className="p-8">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-2">{intl.formatMessage({ id: 'succession.schedule_meeting.date_and_time' })}</h3>
                <p className="text-[var(--succession-text-secondary)]">{intl.formatMessage({ id: 'succession.schedule_meeting.date_and_time_subtitle' })}</p>
              </div>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-[var(--succession-text-primary)] mb-2">
                    {intl.formatMessage({ id: 'succession.schedule_meeting.date_label' })} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      name="date"
                      id="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      className={`block w-full px-4 py-3 border-2 rounded-xl shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 ${
                        errors.date 
                          ? 'border-red-300 focus:border-red-500' 
                          : 'border-[var(--succession-border)]'
                      }`}
                      style={!errors.date ? { '--tw-ring-color': 'var(--succession-primary)', '--tw-ring-opacity': '0.2' } : {}}
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-[var(--succession-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                  {errors.date && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.date}
                    </p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="time" className="block text-sm font-medium text-[var(--succession-text-primary)] mb-3">
                    Time <span className="text-red-500">*</span>
                  </label>
                  
                  {/* Quick Time Selection */}
                  <div className="mb-4">
                    <p className="text-sm text-[var(--succession-text-secondary)] mb-4">{intl.formatMessage({ id: 'succession.schedule_meeting.select_time_slot' })}</p>
                    
                    {/* Morning Slots */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-[var(--succession-text-primary)] mb-2 flex items-center">
                        <svg className="w-4 h-4 mr-2 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        {intl.formatMessage({ id: 'succession.schedule_meeting.morning' })}
                      </h4>
                      <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                        {groupedTimeSlots.morning.map((slot) => (
                          <button
                            key={slot.value}
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, time: slot.value }));
                              if (errors.time) {
                                setErrors(prev => ({ ...prev, time: '' }));
                              }
                            }}
                            className={`px-3 py-2 text-sm font-medium rounded-lg border-2 transition-all duration-200 hover:shadow-sm ${
                              formData.time === slot.value
                                ? 'text-white shadow-md transform scale-105'
                                : 'bg-[var(--succession-bg-primary)] text-[var(--succession-text-primary)] border-[var(--succession-border)] hover:border-[var(--succession-border-hover)] hover:bg-[var(--succession-hover-bg)]'
                            }`}
                            style={formData.time === slot.value ? {
                              backgroundColor: 'var(--succession-primary)',
                              borderColor: 'var(--succession-primary)'
                            } : {}}
                          >
                            {slot.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Afternoon Slots */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-[var(--succession-text-primary)] mb-2 flex items-center">
                        <svg className="w-4 h-4 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        {intl.formatMessage({ id: 'succession.schedule_meeting.afternoon' })}
                      </h4>
                      <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                        {groupedTimeSlots.afternoon.map((slot) => (
                          <button
                            key={slot.value}
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, time: slot.value }));
                              if (errors.time) {
                                setErrors(prev => ({ ...prev, time: '' }));
                              }
                            }}
                            className={`px-3 py-2 text-sm font-medium rounded-lg border-2 transition-all duration-200 hover:shadow-sm ${
                              formData.time === slot.value
                                ? 'text-white shadow-md transform scale-105'
                                : 'bg-[var(--succession-bg-primary)] text-[var(--succession-text-primary)] border-[var(--succession-border)] hover:border-[var(--succession-border-hover)] hover:bg-[var(--succession-hover-bg)]'
                            }`}
                            style={formData.time === slot.value ? {
                              backgroundColor: 'var(--succession-primary)',
                              borderColor: 'var(--succession-primary)'
                            } : {}}
                          >
                            {slot.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Evening Slots */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-[var(--succession-text-primary)] mb-2 flex items-center">
                        <svg className="w-4 h-4 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                        </svg>
                        {intl.formatMessage({ id: 'succession.schedule_meeting.evening' })}
                      </h4>
                      <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                        {groupedTimeSlots.evening.map((slot) => (
                          <button
                            key={slot.value}
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, time: slot.value }));
                              if (errors.time) {
                                setErrors(prev => ({ ...prev, time: '' }));
                              }
                            }}
                            className={`px-3 py-2 text-sm font-medium rounded-lg border-2 transition-all duration-200 hover:shadow-sm ${
                              formData.time === slot.value
                                ? 'text-white shadow-md transform scale-105'
                                : 'bg-[var(--succession-bg-primary)] text-[var(--succession-text-primary)] border-[var(--succession-border)] hover:border-[var(--succession-border-hover)] hover:bg-[var(--succession-hover-bg)]'
                            }`}
                            style={formData.time === slot.value ? {
                              backgroundColor: 'var(--succession-primary)',
                              borderColor: 'var(--succession-primary)'
                            } : {}}
                          >
                            {slot.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Custom Time Input as Fallback */}
                  <div className="border-t pt-4">
                    <p className="text-sm text-[var(--succession-text-secondary)] mb-2">{intl.formatMessage({ id: 'succession.schedule_meeting.custom_time' })}</p>
                    <div className="relative max-w-xs">
                      <input
                        type="time"
                        name="time"
                        id="time"
                        value={formData.time}
                        onChange={handleInputChange}
                        className={`block w-full px-4 py-2 border rounded-lg shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 ${
                          errors.time 
                            ? 'border-red-300' 
                            : 'border-[var(--succession-border)]'
                        }`}
                        style={!errors.time ? { '--tw-ring-color': 'var(--succession-primary)', '--tw-ring-opacity': '0.2' } : {}}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <svg className="w-4 h-4 text-[var(--succession-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {errors.time && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.time}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Duration Section */}
            <div className="p-8">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-2">{intl.formatMessage({ id: 'succession.schedule_meeting.duration' })}</h3>
                <p className="text-[var(--succession-text-secondary)]">{intl.formatMessage({ id: 'succession.schedule_meeting.duration_subtitle' })}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {durationOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 hover:shadow-sm ${
                      parseInt(formData.duration) === option.value
                        ? ''
                        : 'border-[var(--succession-border)] bg-[var(--succession-bg-primary)] hover:border-[var(--succession-border-hover)]'
                    }`}
                    style={parseInt(formData.duration) === option.value ? {
                      borderColor: 'var(--succession-primary)',
                      backgroundColor: 'var(--succession-bg-secondary)'
                    } : {}}
                  >
                    <input
                      type="radio"
                      name="duration"
                      value={option.value}
                      checked={parseInt(formData.duration) === option.value}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className="text-center">
                      <div className={`text-lg font-semibold mb-1 ${
                        parseInt(formData.duration) === option.value ? '' : 'text-[var(--succession-text-primary)]'
                      }`}
                      style={parseInt(formData.duration) === option.value ? { color: 'var(--succession-primary)' } : {}}>
                        {option.label}
                      </div>
                      <div className={`text-xs ${
                        parseInt(formData.duration) === option.value ? 'text-[var(--succession-text-secondary)]' : 'text-[var(--succession-text-muted)]'
                      }`}>
                        {option.subtitle}
                      </div>
                    </div>
                    {parseInt(formData.duration) === option.value && (
                      <div className="absolute top-2 right-2">
                        <svg className="w-4 h-4" style={{ color: 'var(--succession-primary)' }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Location Section (conditional) */}
            {formData.format === 'onsite' && (
              <div className="p-8">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-2">{intl.formatMessage({ id: 'succession.schedule_meeting.location' })}</h3>
                  <p className="text-[var(--succession-text-secondary)]">{intl.formatMessage({ id: 'succession.schedule_meeting.location_subtitle' })}</p>
                </div>
                
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-[var(--succession-text-primary)] mb-2">
                    {intl.formatMessage({ id: 'succession.schedule_meeting.meeting_location' })} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="location"
                      id="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder={intl.formatMessage({ id: 'succession.schedule_meeting.location_placeholder' })}
                      className={`block w-full px-4 py-3 border-2 rounded-xl shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 ${
                        errors.location 
                          ? 'border-red-300 focus:border-red-500' 
                          : 'border-[var(--succession-border)]'
                      }`}
                      style={!errors.location ? { '--tw-ring-color': 'var(--succession-primary)', '--tw-ring-opacity': '0.2' } : {}}
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-[var(--succession-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                  </div>
                  {errors.location && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.location}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Description Section */}
            <div className="p-8">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-2">{intl.formatMessage({ id: 'succession.schedule_meeting.meeting_notes' })}</h3>
                <p className="text-[var(--succession-text-secondary)]">{intl.formatMessage({ id: 'succession.schedule_meeting.meeting_notes_subtitle' })}</p>
              </div>
              
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-[var(--succession-text-primary)] mb-2">
                  {intl.formatMessage({ id: 'succession.schedule_meeting.description_label' })}
                </label>
                <textarea
                  name="description"
                  id="description"
                  rows={4}
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder={intl.formatMessage({ id: 'succession.schedule_meeting.description_placeholder' })}
                  className="block w-full px-4 py-3 border-2 border-[var(--succession-border)] rounded-xl shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 resize-none"
                  style={{ '--tw-ring-color': 'var(--succession-primary)', '--tw-ring-opacity': '0.2' }}
                />
                <p className="mt-2 text-sm text-[var(--succession-text-muted)]">
                  {intl.formatMessage({ id: 'succession.schedule_meeting.description_hint' })}
                </p>
              </div>
            </div>

            {/* Error Display */}
            {errors.submit && (
              <div className="px-8 py-4">
                <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{errors.submit}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="px-8 py-6 bg-[var(--succession-bg-secondary)] flex items-center justify-between">
              <Button
                variant="outline"
                size="lg"
                onClick={() => history.push(`/succession/deals/${deal.deal_name}/prospects/negotiate`)}
                className="flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
                {intl.formatMessage({ id: 'succession.schedule_meeting.cancel' })}
              </Button>
              
              <Button
                as="button"
                type="submit"
                variant="primary"
                size="lg"
                disabled={scheduling}
                className="flex items-center gap-2"
              >
                {scheduling ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {intl.formatMessage({ id: 'succession.schedule_meeting.scheduling' })}
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {intl.formatMessage({ id: 'succession.schedule_meeting.submit' })}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>

      {/* Footer Info */}
      <div className="mt-8 text-center">
        <div className="inline-flex items-center px-4 py-2 bg-[var(--succession-bg-secondary)] rounded-lg border border-[var(--succession-border)]">
          <svg className="w-5 h-5 mr-2" style={{ color: 'var(--succession-primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm text-[var(--succession-text-secondary)]">
            {intl.formatMessage({ id: 'succession.schedule_meeting.calendar_invite_note' })}
          </span>
        </div>
      </div>
    </SingleColumnTemplate>
  );
};

ScheduleMeeting.propTypes = {
  deal: PropTypes.object.isRequired,
  dealInterest: PropTypes.object.isRequired,
  companySettings: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  refetchData: PropTypes.func.isRequired
};

export default ScheduleMeeting; 